import { Controller } from "@hotwired/stimulus"

/**
 * Auto-fills the task Title from the selected Type + Purpose ("Type: Purpose"),
 * mirroring the React task form (use-task-form's auto-title effect), until the
 * rep edits the Title themselves. Shared by the mobile task form and the SQC
 * Validate-tab drawer via pipeline/tasks/_form_fields (CONEK-6782).
 *
 * Markup contract (pipeline/tasks/_form_fields.html.erb):
 *
 *   <div data-controller="pipeline--task-title-autofill">
 *     <select ... data-pipeline--task-title-autofill-target="type"
 *                 data-action="change->pipeline--task-title-autofill#regenerate">
 *     <select ... data-pipeline--task-title-autofill-target="purpose"
 *                 data-action="change->pipeline--task-title-autofill#regenerate">
 *     <input  ... data-pipeline--task-title-autofill-target="title"
 *                 data-action="input->pipeline--task-title-autofill#markEdited">
 *
 * The purpose <select> lives in a turbo-frame that reloads when the type
 * changes; Stimulus re-scans targets on that swap, and picking the new purpose
 * fires `change` → regenerate, so the title fills once BOTH are set.
 *
 * CONEK-7038 added a pre-selected default Purpose on the SQC drawer, which broke
 * both halves of that: on a fresh open no `change` ever fires, so the title stayed
 * empty with both halves known; and after a type change `regenerate` ran against
 * the pre-swap purpose select, leaving a stale "<new Type>: <old Purpose>" that
 * nothing corrected. `purposeTargetConnected` covers both — Stimulus calls it for
 * the initial target AND on every frame swap.
 */
export default class extends Controller {
  static targets = ["type", "purpose", "title"]
  // `prefilled`: the Title arrives pre-filled by the SURFACE, not typed by the rep
  // — the calendar's task drawer seeds it with the booking title (CONEK-7690),
  // exactly like React's TaskDrawer `defaultTaskName`. React still overwrote that
  // seed with "<Type>: <Purpose>" once both were picked (titleManuallyEdited
  // starts false), so a prefilled title does not count as a manual edit; and like
  // React it only ever auto-fills from a picked Purpose, never from the type alone.
  static values = { prefilled: Boolean }

  // In `initialize`, NOT `connect`: Stimulus runs target-connected callbacks for
  // already-present targets around connect time, and `purposeTargetConnected`
  // must never see `this.manual` undefined — on a failed-submit re-render that
  // would auto-fill over the title the rep typed. `initialize` is guaranteed to
  // run first, and runs again on each re-render because the controller element
  // sits inside the replaced #contact-task-form-fields.
  initialize() {
    // A title that already carries a value (edit path, or a failed-submit
    // re-render) is treated as manually set, so auto-fill never clobbers it.
    this.manual =
      !this.prefilledValue &&
      this.hasTitleTarget &&
      this.titleTarget.value.trim() !== ""
  }

  // Fires for the initial purpose select and again each time the turbo-frame
  // swaps in a new one, which is the only signal available when the server
  // pre-selects a purpose instead of the rep picking it.
  purposeTargetConnected() {
    this.regenerate()
  }

  markEdited() {
    this.manual = true
  }

  regenerate() {
    if (this.manual) return
    if (!this.hasTypeTarget || !this.hasPurposeTarget || !this.hasTitleTarget)
      return

    const type = this._selectedText(this.typeTarget)
    const purpose = this._selectedText(this.purposeTarget)
    if (!type) return

    // A blank purpose means two different things, and they need opposite answers.
    // The select is disabled exactly when the type offers no purposes at all
    // (Email, since CONEK-7038): there is nothing to wait for, so the title is the
    // type alone — the same shape Tasks::Create builds server-side when it
    // auto-names a purposeless task. An ENABLED select with nothing chosen means
    // the rep simply has not picked yet, so leave the title alone rather than
    // writing a half-answer.
    if (!purpose) {
      if (!this.purposeTarget.disabled || this.prefilledValue) return
      this.titleTarget.value = type
      return
    }

    // Setting `.value` programmatically does not fire `input`, so this does not
    // trip `markEdited` — auto-fill stays live until the rep actually types.
    this.titleTarget.value = `${type}: ${purpose}`
  }

  // The visible text of the chosen option, or "" for the empty/placeholder one.
  _selectedText(select) {
    const option = select.options && select.options[select.selectedIndex]
    if (!option || option.value === "") return ""
    return option.text.trim()
  }
}
